import React, { useEffect } from "react";
import { Button, Col, Row } from "reactstrap";
import SelectBox from "../../../components/SelectBox";
import { LoggedUserType } from "../../../models/GlobalModels";
import SendIcon from "@mui/icons-material/Send";
import DownloadIcon from "@mui/icons-material/Download";
import MultiSelectDropdown from "../../../components/MultiSelectDropdown";
import {
  blocksByDistrictDataAPI,
  dashboardMetricsList,
  districtsByStateDataAPI,
  icvGetAllSeasonsDataAPICall,
  icvGetStatesDataAPICall,
  icvGetYearDataAPICall,
  villagesByBlocksAPI,
} from "../../../api/icvActivityAPI";
import { useDispatch, useSelector } from "react-redux";
import { FormUtils } from "../../../utils/FormUtils";
import { DEFAULT_OPTION } from "../../../utils/Constant";
import { loginSelector } from "../../../redux/slices/loginSlice";
import { DashboardFilterListPayload } from "../../../models/ICVDashboard";
import { Controller, useForm } from "react-hook-form";
import { CircleSpinnerOverlay } from "react-spinner-overlay";
import { handleDashboardMetricsDataSlice } from "../../../redux/slices/icvActivitySlice";

function ICVDashboard() {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors, isValid },
  } = useForm<DashboardFilterListPayload>({
    defaultValues: {
      season: "",
      year: "",
      stateId: [],
      districtIds: [],
      blockIds: [],
      villageIds: [],
    },
    mode: "all",
  });
  const selectedState = watch("stateId");
  const selectedDistrict = watch("districtIds");
  const selectedTehsilBlock = watch("blockIds");
  const selectedVillage = watch("villageIds");
  const {
    storeICVGetStatesData,
    storeICVGetSeasonsData,
    storeICVGetYearData,
    storeICVvillagesByBlocks,
    storeICVDistrictsByStateData,
    storeICVBlocksByDistricData,
    storeDashboardMetrics,
    isCrudLoading,
  } = useSelector((state: any) => state.icvActivity);
  const metricsData = storeDashboardMetrics?.data;
  
  const dispatch: any = useDispatch();
  const user: LoggedUserType = useSelector(loginSelector).user;

  // Effects
  useEffect(() => {
    dispatch(icvGetStatesDataAPICall());
    dispatch(icvGetAllSeasonsDataAPICall());
    dispatch(icvGetYearDataAPICall());
  }, [dispatch]);

  // Restricting the Districts  when state is removed from the state dropdown
  useEffect(() => {
    if (!multiDistrictsData?.length) return;

    const filteredDistricts: any = selectedDistrict.filter((district: any) => {
      return selectedState.some(
        (state: any) => state.value === district.stateId,
      );
    });

    setValue("districtIds", filteredDistricts);
  }, [selectedState]);

  // Restricting the Tehsil/Blocks when district is removed from district dropdown
  useEffect(() => {
    if (!multiBlocksData?.length) return;

    const filteredBlocks: any = selectedTehsilBlock.filter((block: any) => {
      return selectedDistrict.some(
        (district: any) => district.value === block.districtId,
      );
    });

    setValue("blockIds", filteredBlocks);
  }, [selectedDistrict]);

  // Restricting the Villages when tehsil/block is removed from tehsil/block dropdown
  useEffect(() => {
    if (!multiVillagesData?.length) return;

    const filteredVillages: any = selectedVillage.filter((village: any) => {
      return selectedTehsilBlock.some(
        (block: any) => block.value === village.blockId,
      );
    });

    setValue("villageIds", filteredVillages);
  }, [selectedTehsilBlock]);

  // Resetting the form and dashboard data on component unmount
  useEffect(() => {
    return () => {
      handleReset();
    };
  }, []);

  const statesData = FormUtils.convertDataList(
    storeICVGetStatesData?.data ?? [],
  );

  // Multi States, Districts and village - Vendor Allocation
  const multiDistrictsData = FormUtils.convertMultiDataDistrictList(
    storeICVDistrictsByStateData?.data ?? [],
  );

  const multiBlocksData = FormUtils.convertMultiBlocksList(
    storeICVBlocksByDistricData?.data ?? [],
  );

  const multiVillagesData = FormUtils.convertMultiVillagesList(
    storeICVvillagesByBlocks?.data ?? [],
  );

  const seasonsData = FormUtils.convertDataList(
    storeICVGetSeasonsData?.data ?? [],
  );
  const yearData = FormUtils.convertToDataListObject(
    storeICVGetYearData?.data ?? [],
  );

  // Handlers
  const onchangeDropdown = (label: string, event: any) => {
    switch (label) {
      case "State":
        dispatch(districtsByStateDataAPI([event.value]));

        break;

      case "District":
        const stateCodesfromDistricts: string[] = Array.from(
          new Set(event.map((item: any) => item.stateId)),
        );

        const districtCodes1: string[] = Array.from(
          new Set(event.map((item: any) => item.value)),
        );

        dispatch(
          blocksByDistrictDataAPI(stateCodesfromDistricts, districtCodes1),
        );

        break;

      case "tehsilBlock":
        const stateCodes: string[] = Array.from(
          new Set(event.map((item: any) => item.stateId)),
        );

        const districtCodes: string[] = Array.from(
          new Set(event.map((item: any) => item.districtId)),
        );

        const blockCodes: string[] = Array.from(
          new Set(event.map((item: any) => item.value)),
        );
        dispatch(villagesByBlocksAPI(stateCodes, districtCodes, blockCodes));

        break;

      default:
        break;
    }
  };

  const onSubmit = (data: any) => {
    dispatch(dashboardMetricsList(data));
  };

  const handleReset = () => {
    reset();
    dispatch(handleDashboardMetricsDataSlice([]));
  };

  return (
    <>
      {isCrudLoading && (
        <CircleSpinnerOverlay
          loading={isCrudLoading}
          overlayColor="rgba(147, 166, 199, 0.1)"
          color="#5C79AA"
          zIndex={9999}
        />
      )}
      <div className="filter-section">
        <Row>
          {/* Season dropdown*/}
          <Col xs={6} md={3}>
            <Controller
              name="season"
              control={control}
              rules={{
                validate: (val: any) =>
                  val?.value ? true : "Season is required",
              }}
              render={({ field, fieldState: { error } }) => (
                <SelectBox
                  {...field}
                  label="Season"
                  required={true}
                  options={seasonsData}
                  className="css-select-bg"
                />
              )}
            />
          </Col>

          {/* Year dropdown */}
          <Col xs={6} md={3}>
            <Controller
              name="year"
              control={control}
              rules={{
                validate: (val: any) =>
                  val?.value ? true : "Year is required",
              }}
              render={({ field, fieldState: { error } }) => (
                <SelectBox
                  {...field}
                  label="Year"
                  required={true}
                  options={yearData}
                  className="css-select-bg"
                />
              )}
            />
          </Col>

          {/* State dropdown */}
          <Col xs={6} md={3}>
            <Controller
              name="stateId"
              control={control}
              rules={{
                validate: (val: any) =>
                  val?.value ? true : "State is required",
              }}
              render={({ field, fieldState: { error } }) => (
                <SelectBox
                  {...field}
                  label="State"
                  required={true}
                  options={statesData}
                  className="css-select-bg"
                  onChange={(event: any) => {
                    if (event != null) {
                      setValue("stateId", event, {
                        shouldValidate: true,
                      });

                      onchangeDropdown("State", event);
                    } else {
                      setValue("stateId", [DEFAULT_OPTION]);
                    }
                  }}
                />
              )}
            />
          </Col>

          {/* Multi Selection District Dropdown */}
          <Col xs={6} md={3}>
            <Controller
              name="districtIds"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <MultiSelectDropdown
                  {...field}
                  fromVendorCreation
                  disabled={Number(selectedState.length) === 0}
                  label="District"
                  options={multiDistrictsData}
                  onChange={(event: any) => {
                    if (event != null) {
                      setValue("districtIds", event, {
                        shouldValidate: true,
                      });
                      onchangeDropdown("District", event);
                    } else {
                      setValue("districtIds", [DEFAULT_OPTION]);
                    }
                  }}
                />
              )}
            />
          </Col>

          {/* Multi Selection Tehsil/Block Dropdown */}
          <Col xs={6} md={3}>
            <Controller
              name="blockIds"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <MultiSelectDropdown
                  {...field}
                  fromVendorCreation
                  disabled={Number(selectedDistrict.length) === 0}
                  label="Tehsil/Block"
                  options={multiBlocksData}
                  onChange={(event: any) => {
                    if (event != null) {
                      setValue("blockIds", event, {
                        shouldValidate: true,
                      });
                      onchangeDropdown("tehsilBlock", event);
                    } else {
                      setValue("blockIds", [DEFAULT_OPTION]);
                    }
                  }}
                />
              )}
            />
          </Col>

          {/* Multi Selection Village Dropdown */}
          <Col xs={6} md={3}>
            <Controller
              name="villageIds"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <MultiSelectDropdown
                  {...field}
                  fromVendorCreation
                  disabled={Number(selectedTehsilBlock.length) === 0}
                  label="Village"
                  options={multiVillagesData}
                  onChange={(event: any) => {
                    if (event != null) {
                      setValue("villageIds", event, {
                        shouldValidate: true,
                      });
                    } else {
                      setValue("villageIds", [DEFAULT_OPTION]);
                    }
                  }}
                />
              )}
            />
          </Col>

          <Col xs={6} md={3} className="d-flex align-items-end gap-2 mb-2">
            <Button
              variant="contained"
              disabled={!isValid}
              className=" cl-btn-primary d-flex align-items-center justify-content-center"
              onClick={handleSubmit((data) => onSubmit(data))}
            >
              Submit
              <SendIcon sx={{ fontSize: "18px", ml: 1 }} />
            </Button>
          </Col>
        </Row>
      </div>
      {metricsData?.length > 0 && (
        <div className="filter-section" style={{ marginTop: 20 }}>
          {/* <div className="d-flex justify-content-end mb-3">
          <Button
            variant="contained"
            className="btn cl-btn-outline-primary d-flex align-items-center justify-content-center"
          >
            Download Report
            <DownloadIcon sx={{ fontSize: "18px", ml: 1 }} />
          </Button>
        </div> */}

          {/* Cards */}
          <div className="row g-3">
            {metricsData?.map((card: any, index: number) => (
              <Col xs={6} md={3} key={index}>
                <div
                  style={{
                    background: `${card?.color}`,
                    borderRadius: "12px",
                    color: "#fff",
                    padding: "20px",
                    textAlign: "center",
                    height: "100%",
                  }}
                >
                  <div style={{ fontSize: "16px", marginBottom: "10px" }}>
                    {card?.label}
                  </div>
                  <div style={{ fontSize: "32px", fontWeight: "bold" }}>
                    {card?.value}
                  </div>
                </div>
              </Col>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

export default ICVDashboard;
